import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import type { ReactNode } from "react";
import { SectionTitle } from "@/components/ui-typography";
import { visualCard } from "./visual-styles";

const useStyles = makeStyles({
  card: visualCard,
  head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: tokens.spacingHorizontalL },
  titles: { display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  caption: { color: tokens.colorNeutralForeground3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  titleRow: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, minWidth: 0 },
  title: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  actions: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, flexShrink: 0 },
  metrics: {
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalXS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  /** 위에 기준·제목·판정이 없으면 구분선이 카드 맨 위에 붙는다. */
  metricsOnly: { paddingTop: 0, borderTopStyle: "none" },
});

/**
 * 화면 첫 카드(헤드라인). 요약·분석·리포트가 같은 순서로 읽힌다:
 * 기준(캡션) → 대상(제목) → 판정 줄 → 핵심 지표 → 부가 설명(children).
 */
export function VisualHeadline({
  ariaLabel,
  caption,
  title,
  titleAddon,
  actions,
  verdict,
  metrics,
  children,
  className,
}: {
  ariaLabel: string;
  caption?: ReactNode;
  title?: ReactNode;
  titleAddon?: ReactNode;
  actions?: ReactNode;
  verdict?: ReactNode;
  metrics?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const st = useStyles();
  const hasTitles = caption != null || title != null || titleAddon != null;
  const hasHead = hasTitles || actions != null;
  return (
    <section className={mergeClasses(st.card, className)} aria-label={ariaLabel}>
      {hasHead && (
        <div className={st.head}>
          {hasTitles && (
            <div className={st.titles}>
              {caption != null && <Caption1 className={st.caption}>{caption}</Caption1>}
              {(title != null || titleAddon != null) && (
                <div className={st.titleRow}>
                  {title != null && <SectionTitle className={st.title}>{title}</SectionTitle>}
                  {titleAddon}
                </div>
              )}
            </div>
          )}
          {actions && <div className={st.actions}>{actions}</div>}
        </div>
      )}
      {verdict}
      {metrics && <div className={mergeClasses(st.metrics, !hasHead && !verdict && st.metricsOnly)}>{metrics}</div>}
      {children}
    </section>
  );
}
